/**
 * Aba Equipe: quem acessa o painel da empresa, com o papel Comercial
 * (vendedora) em destaque e convite local.
 *
 * Decisoes nao obvias:
 *  - Diferente da aba homonima das Configuracoes do lojista, aqui NAO ha
 *    rascunho com "Salvar": a administracao convida e pronto, e a linha nova
 *    recebe o selo "Convite na previa". Quando a rota existir, o clique vira
 *    `POST /api/admin/companies/:id/team` e o selo passa a "Convite enviado".
 *  - O bloco "Vendedoras" vem ANTES da tabela (ADR-001 E5, 25/09/2026): o
 *    WhatsApp conectado costuma ser o do comercial, quem responde a um toque
 *    cai com a vendedora, e a venda fechada por ela continua creditada ao
 *    ultimo toque do motor. "Fechado por" e informacao adicional, nunca
 *    substitui o "creditado ao contato". A equipe precisa ver isso escrito.
 *  - "Convidar vendedora" abre o mesmo convite ja no papel Comercial: e o
 *    convite que mais falta nas contas novas.
 *  - Filtro por papel com contagem e busca quando a equipe passa de seis
 *    pessoas: empresa grande tem dezenas de vendedoras.
 *  - Papel `platform_admin` nao entra no convite: e a nossa equipe.
 *  - Os convites moram no DETALHE (`convidados` + `onConvidar`), nao aqui: a
 *    contagem da aba e a saude da empresa ("vendedora cadastrada") precisam
 *    enxergar a vendedora recem-convidada, senao o Resumo continua pedindo
 *    para convidar quem acabou de ser convidada.
 */

import { useState } from 'react';
import { Badge, Button, Field, FilterChips, Icon, Input, Modal, Panel, SectionCard, Select, type ChipOption } from '../../../components/ui';
import { formatCount, formatDateTime } from '../../../lib/format';
import type { TeamMember, UserRole } from '../../../types';
import { ROLE_INFO, ROLES_CONVIDAVEIS, emailValido, normalizar } from '../vocabulario';

const PREFIXO_CONVITE = 'SIMULADO-convite-';
const FORM_ID = 'form-convite-admin';
const BUSCA_A_PARTIR_DE = 7;

type PapelConvidavel = Exclude<UserRole, 'platform_admin'>;
type FiltroPapel = 'todos' | UserRole;

interface Convite {
  readonly nome: string;
  readonly email: string;
  readonly papel: PapelConvidavel;
}

function ehPapelConvidavel(valor: string): valor is PapelConvidavel {
  return ROLES_CONVIDAVEIS.some((r) => r === valor);
}

function PapelBadge({ role }: { readonly role: UserRole }): JSX.Element {
  const info = ROLE_INFO[role];
  return (
    <span title={info.hint}>
      <Badge tone={info.tone}>{info.label}</Badge>
    </span>
  );
}

export function AbaEquipe({
  base,
  convidados,
  onConvidar,
  companyId,
  companyName,
}: {
  readonly base: readonly TeamMember[];
  /** Convites feitos nesta visita (estado do detalhe). */
  readonly convidados: readonly TeamMember[];
  readonly onConvidar: (novo: TeamMember) => void;
  readonly companyId: string;
  readonly companyName: string;
}): JSX.Element {
  const [convidando, setConvidando] = useState(false);
  const [convite, setConvite] = useState<Convite>({ nome: '', email: '', papel: 'member' });
  const [tentou, setTentou] = useState(false);
  const [filtro, setFiltro] = useState<FiltroPapel>('todos');
  const [busca, setBusca] = useState('');
  const [avisoConvite, setAvisoConvite] = useState<string | null>(null);

  const membros = [...base, ...convidados];
  const vendedoras = membros.filter((m) => m.role === 'comercial');
  const emailNormalizado = convite.email.trim().toLowerCase();
  const erroNome = convite.nome.trim() === '' ? 'Informe o nome.' : null;
  const erroEmail = !emailValido(convite.email) ? 'Informe um e-mail válido.' : membros.some((m) => m.email.toLowerCase() === emailNormalizado) ? 'Essa pessoa já está na equipe.' : null;
  const valido = erroNome === null && erroEmail === null;

  const termo = normalizar(busca);
  const visiveis = membros.filter((m) => (filtro === 'todos' || m.role === filtro) && (termo === '' || normalizar(`${m.name} ${m.email}`).includes(termo)));

  const contar = (papel: FiltroPapel): string => formatCount(membros.filter((m) => papel === 'todos' || m.role === papel).length);
  const opcoes: readonly ChipOption<FiltroPapel>[] = [
    { value: 'todos', label: 'Todos', count: contar('todos') },
    { value: 'owner', label: ROLE_INFO.owner.plural, count: contar('owner') },
    { value: 'member', label: ROLE_INFO.member.plural, count: contar('member') },
    { value: 'comercial', label: ROLE_INFO.comercial.plural, count: contar('comercial') },
  ];

  const abrir = (papel: PapelConvidavel): void => {
    setConvite({ nome: '', email: '', papel });
    setTentou(false);
    setConvidando(true);
  };

  const fechar = (): void => {
    setConvidando(false);
    setTentou(false);
  };

  const confirmar = (): void => {
    setTentou(true);
    if (!valido) {
      return;
    }
    const novo: TeamMember = {
      id: `${PREFIXO_CONVITE}${convidados.length + 1}`,
      companyId,
      name: convite.nome.trim(),
      email: emailNormalizado,
      role: convite.papel,
      lastAccessAt: null,
    };
    onConvidar(novo);
    setAvisoConvite(`${novo.name} entrou na equipe da prévia com papel ${ROLE_INFO[novo.role].label}.`);
    fechar();
  };

  const ehConvite = (m: TeamMember): boolean => m.id.startsWith(PREFIXO_CONVITE);
  const acessoDe = (m: TeamMember): string => (m.lastAccessAt === null ? (ehConvite(m) ? 'Convite pendente' : 'Nunca acessou') : formatDateTime(m.lastAccessAt));

  return (
    <div className="flex flex-col gap-4">
      <Panel tone={vendedoras.length === 0 ? 'warning' : 'accent'} className="flex flex-col gap-4 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <span aria-hidden="true" className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-accent/15 text-accent">
              <Icon name="resposta" size="md" />
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-ink">
                Vendedoras (papel Comercial) · <span className="tabular-nums">{formatCount(vendedoras.length)}</span>
              </h2>
              <p className="mt-0.5 max-w-3xl text-sm text-ink-muted">
                O WhatsApp conectado costuma ser o número do comercial. Quando o lead responde a um toque da régua e fecha com a vendedora, a comissão continua do Converte Fácil,
                creditada ao último toque do motor. “Fechado por” fica registrado como informação adicional. Venda sem webhook (Pix manual, outro link) a vendedora registra como
                venda assistida, pelo mesmo caminho de atribuição.
              </p>
            </div>
          </div>
          <Button variant="secondary" icon="mais" onClick={() => abrir('comercial')}>
            Convidar vendedora
          </Button>
        </div>
        {vendedoras.length === 0 ? (
          <p className="flex items-start gap-2 text-sm text-ink">
            <Icon name="alerta" size="sm" className="mt-0.5 shrink-0 text-status-warning" />
            <span>
              Nenhuma vendedora em {companyName}. Quem responde à régua cai no WhatsApp do comercial sem ninguém no painel para assumir a conversa e registrar a venda assistida.
            </span>
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {vendedoras.map((v) => (
              <li key={v.id} className="flex items-center gap-2 rounded-full border border-line bg-surface py-1 pl-1 pr-3 text-sm">
                <span aria-hidden="true" className="grid h-7 w-7 place-items-center rounded-full bg-accent/15 text-[11px] font-semibold text-accent">
                  {v.name.trim().charAt(0).toUpperCase()}
                </span>
                <span className="text-ink">{v.name}</span>
                <span className="text-xs text-ink-subtle">· {acessoDe(v)}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <span role="status" className="sr-only">
        {avisoConvite ?? ''}
      </span>
      {avisoConvite === null ? null : (
        <p className="flex items-center gap-2 text-sm text-ink" aria-hidden="true">
          <Badge tone="good" icon="check">
            Convite na prévia
          </Badge>
          {avisoConvite} Nada foi enviado.
        </p>
      )}

      <SectionCard
        id="equipe"
        title="Equipe"
        icon="usuarios"
        description={`Quem acessa o painel de ${companyName}. Dono altera configurações; equipe acompanha funis, leads e contatos; comercial atende as respostas da régua.`}
        aside={
          <Button variant="primary" icon="mais" onClick={() => abrir('member')}>
            Convidar
          </Button>
        }
        padded={false}
      >
        <div className="flex flex-col gap-3 px-5 pb-4 md:flex-row md:items-center md:justify-between">
          <FilterChips label="Filtrar equipe por papel" prefix="Papel:" options={opcoes} value={filtro} onChange={setFiltro} />
          {membros.length >= BUSCA_A_PARTIR_DE ? (
            <div className="relative md:w-64">
              <label htmlFor="busca-equipe" className="sr-only">
                Buscar pessoa por nome ou e-mail
              </label>
              <Icon name="busca" size="sm" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
              <Input id="busca-equipe" size="sm" type="search" placeholder="Nome ou e-mail" value={busca} onChange={(e) => setBusca(e.target.value)} className="pl-9" />
            </div>
          ) : null}
        </div>

        {visiveis.length === 0 ? (
          <div className="flex flex-col items-start gap-3 px-5 pb-6">
            <p className="text-sm text-ink-muted">
              {membros.length === 0
                ? 'Ninguém na equipe. Convide o dono da conta e a vendedora que atende o WhatsApp do comercial.'
                : filtro === 'comercial'
                  ? 'Nenhuma vendedora ainda. É quem assume as respostas da régua e registra a venda assistida.'
                  : 'Ninguém com esse filtro.'}
            </p>
            {filtro === 'comercial' || membros.length === 0 ? (
              <Button variant="secondary" size="sm" icon="mais" onClick={() => abrir(filtro === 'comercial' ? 'comercial' : 'owner')}>
                {filtro === 'comercial' ? 'Convidar vendedora' : 'Convidar o dono'}
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                icon="x"
                onClick={() => {
                  setFiltro('todos');
                  setBusca('');
                }}
              >
                Limpar filtro
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-y border-line text-left text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">
                    <th scope="col" className="px-5 py-2">
                      Nome
                    </th>
                    <th scope="col" className="px-3 py-2">
                      E-mail
                    </th>
                    <th scope="col" className="px-3 py-2">
                      Papel
                    </th>
                    <th scope="col" className="px-5 py-2">
                      Último acesso
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {visiveis.map((m) => (
                    <tr key={m.id}>
                      <th scope="row" className="px-5 py-3 text-left font-medium text-ink">
                        <span className="flex flex-wrap items-center gap-2">
                          {m.name}
                          {ehConvite(m) ? (
                            <Badge tone="info" dot>
                              Convite na prévia
                            </Badge>
                          ) : null}
                        </span>
                      </th>
                      <td className="px-3 py-3 text-ink-muted">{m.email}</td>
                      <td className="px-3 py-3">
                        <PapelBadge role={m.role} />
                      </td>
                      <td className="px-5 py-3 text-ink-muted">{acessoDe(m)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="divide-y divide-line border-t border-line md:hidden">
              {visiveis.map((m) => (
                <li key={m.id} className="flex flex-col gap-2 px-5 py-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium text-ink">{m.name}</p>
                    <PapelBadge role={m.role} />
                  </div>
                  <p className="break-all text-sm text-ink-muted">{m.email}</p>
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ink-subtle">
                    <span>Último acesso: {acessoDe(m)}</span>
                    {ehConvite(m) ? (
                      <Badge tone="info" dot>
                        Convite na prévia
                      </Badge>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </SectionCard>

      <Modal
        open={convidando}
        onClose={fechar}
        title={convite.papel === 'comercial' ? 'Convidar vendedora' : 'Convidar para a equipe'}
        description={`A pessoa recebe um e-mail para criar a senha e entrar no painel de ${companyName}. Na prévia, nada é enviado.`}
        footer={
          <>
            <Button variant="ghost" onClick={fechar}>
              Cancelar
            </Button>
            <Button variant="primary" icon="mais" type="submit" form={FORM_ID}>
              Adicionar à prévia
            </Button>
          </>
        }
      >
        <form
          id={FORM_ID}
          className="flex flex-col gap-4"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            confirmar();
          }}
        >
          <Field label="Nome" required error={tentou ? erroNome : null}>
            {({ id, describedBy, invalid }) => (
              <Input id={id} aria-describedby={describedBy} aria-invalid={invalid} value={convite.nome} onChange={(e) => setConvite((c) => ({ ...c, nome: e.target.value }))} autoComplete="off" />
            )}
          </Field>
          <Field label="E-mail" required error={tentou ? erroEmail : null}>
            {({ id, describedBy, invalid }) => (
              <Input id={id} type="email" inputMode="email" aria-describedby={describedBy} aria-invalid={invalid} value={convite.email} onChange={(e) => setConvite((c) => ({ ...c, email: e.target.value }))} autoComplete="off" />
            )}
          </Field>
          <Field label="Papel" hint={ROLE_INFO[convite.papel].hint}>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={convite.papel}
                onChange={(e) => {
                  const valor = e.target.value;
                  if (ehPapelConvidavel(valor)) {
                    setConvite((c) => ({ ...c, papel: valor }));
                  }
                }}
              >
                {ROLES_CONVIDAVEIS.map((papel) => (
                  <option key={papel} value={papel}>
                    {ROLE_INFO[papel].label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {convite.papel === 'comercial' ? (
            <p className="flex items-start gap-2 rounded-lg border border-accent/40 bg-accent/5 px-3 py-2 text-xs text-ink-muted">
              <Icon name="info" size="xs" className="mt-0.5 shrink-0 text-accent" />
              <span>A vendedora vê a fila de respostas da régua, assume conversas e registra venda assistida. Ela não altera configurações nem a comissão.</span>
            </p>
          ) : null}
        </form>
      </Modal>
    </div>
  );
}
